import React from 'react';
import Footer from 'components/Footer';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';
import { AssetUrls } from 'config/assets';
import { BRAND_GRADIENT } from 'screens/Public/Brand/brandData';
import './topdecked.css';

// Sign-up destination. The UTM tags identify TopDecked-sourced traffic and
// match the utm_source capture in AnalyticsTracker (src/routes/index.tsx),
// keeping attribution consistent with other partnership links. A full anchor
// navigation (not SPA routing) is intentional so the analytics tracker
// re-reads utm_source on landing.
const SIGNUP_HREF =
  '/welcome?utm_source=topdecked&utm_medium=partnership&utm_campaign=integration';

// How TradeMagic works, in newcomer-friendly terms.
const STEPS: { title: string; body: string }[] = [
  {
    title: 'Bring your collection',
    body: 'Import the cards you already track in TopDecked. Your binder becomes a trade-ready list in a few minutes.',
  },
  {
    title: 'List what you want to move',
    body: 'Mark the cards you own but are not using. Those are the cards you will trade for value with other players.',
  },
  {
    title: 'Trade for value',
    body: 'Every card has a point value, where 100 points equals one US dollar. Trade your cards for points, then put those points toward the cards you want.',
  },
];

const TopDeckedPage: React.FC = () => {
  return (
    <>
      <SEOHelmet
        title="TradeMagic x TopDecked - Trade the Cards You Own"
        description="You cataloged your collection in TopDecked. Put it to work on TradeMagic: trade the cards you are not using for the ones you want, in a peer-to-peer points economy."
        canonical="https://trademagic.gg/topdecked"
        keywords="TradeMagic, TopDecked, MTG trading, Magic the Gathering trade, peer to peer card trading"
        type="website"
      />

      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg/' },
          { name: 'TopDecked', url: 'https://trademagic.gg/topdecked' },
        ]}
      />

      <div className="td-page">
        {/* Co-brand lockup. TradeMagic mark on the left, TopDecked staged as a
            clearly-marked placeholder on the right until Lincoln sends the
            approved logo and usage permission. */}
        <header className="td-topbar">
          <div className="td-lockup">
            <span className="td-brand">
              <img
                className="td-brand-icon"
                src={AssetUrls.logos.iconGold}
                alt="TradeMagic"
                width={32}
                height={32}
              />
              <span className="td-brand-word">TradeMagic</span>
            </span>

            <span className="td-lockup-x" aria-hidden="true">
              &times;
            </span>

            {/* TODO(topdecked-logo): swap this placeholder for the approved
                TopDecked logo once received. Replace the inner contents of
                .td-partner-placeholder with an <img>; the reserved box keeps
                the layout intact. */}
            <span
              className="td-partner-placeholder"
              role="img"
              aria-label="TopDecked logo pending"
            >
              [TopDecked logo pending]
            </span>
          </div>
        </header>

        <main className="td-main">
          {/* Hero */}
          <section className="td-hero">
            <h1 className="td-hero-title">
              You cataloged your collection. Now put it to work.
            </h1>
            <p className="td-hero-sub">
              TradeMagic turns the cards sitting in your TopDecked binder into
              the ones you actually want. List the cards you own but are not
              using, and trade them for value with other players.
            </p>
            <div className="td-cta-row">
              <a
                className="td-btn td-btn-primary"
                href={SIGNUP_HREF}
                style={{ background: BRAND_GRADIENT }}
              >
                Start trading on TradeMagic
              </a>
            </div>
            <p className="td-hero-note">
              Free to join. Bring the collection you already track in TopDecked.
            </p>
          </section>

          {/* How it works */}
          <section className="td-section">
            <h2 className="td-section-title">How TradeMagic works</h2>
            <p className="td-section-lead">
              TradeMagic is peer-to-peer trading built on a simple points
              economy. You trade the cards you own for points, and spend points
              on the cards you want. No auctions, no bidding wars.
            </p>
            <div className="td-steps">
              {STEPS.map((step, index) => (
                <div className="td-step" key={step.title}>
                  <div className="td-step-num">{index + 1}</div>
                  <h3 className="td-step-title">{step.title}</h3>
                  <p className="td-step-body">{step.body}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Closing CTA */}
          <section className="td-closing">
            <h2 className="td-closing-title">Trade what you own for what you want</h2>
            <p className="td-closing-sub">
              Your TopDecked collection is already a trade list waiting to
              happen. Create a free account and start moving cards today.
            </p>
            <div className="td-cta-row">
              <a
                className="td-btn td-btn-primary"
                href={SIGNUP_HREF}
                style={{ background: BRAND_GRADIENT }}
              >
                Create your free account
              </a>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default TopDeckedPage;
